/** @format */

import React, { useEffect, useState } from "react";
import { Alert, Form, Modal, Button } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useUpdateResourceMutation } from "../../../app/services/resource/resourcesApi";
import { useSaveUserResourceMutation } from "../../../app/services/userResource/userResourcesApi";
import { Resource, UserResource } from "../../resources/types";

type Props = {
  resource: Resource | UserResource;
  userResource: boolean;
};

const StatusSwitch: React.FC<Props> = ({ resource, userResource }) => {
  const { t: _ } = useTranslation();

  const [showStatusChangeModal, setShowStatusChangeModal] =
    useState<boolean>(false);
  const [isResourceLive, setIsResourceLive] = useState<boolean>();

  const [saveResource] = useUpdateResourceMutation();
  const [saveUserResource] = useSaveUserResourceMutation();

  useEffect(() => {
    if (resource) setIsResourceLive(resource.Status === "live");
  }, [resource]);

  const updateStatus = async () => {
    if (resource) {
      try {
        if (!userResource) {
          await saveResource({
            diff: {
              Status: isResourceLive
                ? "live"
                : ("draft" as "live" | "draft" | undefined),
            },
            primaryKey: {
              PK: resource.PK,
              SK: resource.SK,
            },
          });
        } else {
          await saveUserResource({
            diff: {
              Status: isResourceLive
                ? "live"
                : ("draft" as "live" | "draft" | undefined),
            },
            primaryKey: { PK: resource.PK, SK: resource.SK },
            userEmailHash: (resource as UserResource).UserEmailHash,
            locale: (resource as UserResource).Locale,
            copy: (resource as UserResource).Copy,
          });
        }
        setShowStatusChangeModal(false);
      } catch {
        console.log("Failed to save resource");
      }
    }
  };

  return (
    <>
      <div className={`d-flex ${userResource && "py-2"}`}>
        <span
          className={`flex-grow-1 text-start ${
            userResource ? "text-black pe-2" : "text-editing"
          }`}
        >
          {_("Status")}:
        </span>
        <Form.Switch
          className="pe-2 text-editing"
          onChange={() => {
            setIsResourceLive(!isResourceLive);
            setShowStatusChangeModal(true);
          }}
          label={
            isResourceLive ?? resource?.Status === "live"
              ? _("LIVE")
              : _("DRAFT")
          }
          checked={isResourceLive ?? resource?.Status === "live"}
        />
      </div>

      <Modal
        dialogClassName="delete-modal"
        show={showStatusChangeModal}
        onHide={() => {
          setIsResourceLive(!isResourceLive);
          setShowStatusChangeModal(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          {_("Change resource's status")}?
        </Modal.Header>
        <Modal.Body>
          <Alert variant={isResourceLive ? "danger" : "warning"}>
            <Trans
              i18nKey="statusSure"
              components={{ code: <code /> }}
              values={{
                status: _(resource.Status),
                notStatus: isResourceLive ? _("live") : _("draft"),
              }}
            />
          </Alert>
          <div className="mt-3 justify-content-end d-flex gap-2">
            <Button
              variant="default"
              onClick={() => {
                setIsResourceLive(!isResourceLive);
                setShowStatusChangeModal(false);
              }}
            >
              {_("Cancel")}
            </Button>
            <Button variant="editing" onClick={() => updateStatus()}>
              {_("Update")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default StatusSwitch;
